import { NgModule } from '@angular/core';
import { Apollo, APOLLO_OPTIONS } from 'apollo-angular';
import { ApolloClient, ApolloClientOptions, DefaultOptions, InMemoryCache, Observable as ApolloObservable } from '@apollo/client/core';
import { ApolloLink, from } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { HttpLink } from 'apollo-angular/http';
import { environment } from 'src/environments/environment';
import { AuthService } from './services/auth.service';
const uri = `${environment.graphql_apiUrl}/graphql`;

const defaultOptions: DefaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'none',
  },
  query: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'none',
  },
}

export function createApollo(httpLink: HttpLink, auth: AuthService): ApolloClientOptions<any> {
  const authLink = new ApolloLink((operation, forward) => {
    if (operation.operationName === 'RequestJBagotAccess') {
      return forward(operation);
    }

    return new ApolloObservable((observer) => {
      let requestSubscription: { unsubscribe(): void } | undefined;
      const tokenSubscription = auth.getAccessToken().subscribe({
        next: (token) => {
          operation.setContext(({ headers = {} }) => ({
            headers: { ...headers, Authorization: `Bearer ${token}` },
            jBagotAccessToken: token,
          }));
          requestSubscription = forward(operation).subscribe(observer);
        },
        error: (error) => observer.error(error),
      });

      return () => {
        tokenSubscription.unsubscribe();
        requestSubscription?.unsubscribe();
      };
    });
  });

  const tokenErrorLink = onError(({ graphQLErrors, operation, forward }) => {
    const expired = graphQLErrors?.some(
      (error) => error.message.toLowerCase() === 'jwt expired'
    );
    if (!expired || operation.getContext()['jBagotRetried']) {
      return undefined;
    }

    return new ApolloObservable((observer) => {
      let retrySubscription: { unsubscribe(): void } | undefined;
      const oldToken = operation.getContext()['jBagotAccessToken'];
      const tokenSubscription = auth.refreshAccessToken(oldToken).subscribe({
        next: (token) => {
          operation.setContext(({ headers = {} }) => ({
            headers: { ...headers, Authorization: `Bearer ${token}` },
            jBagotAccessToken: token,
            jBagotRetried: true,
          }));
          retrySubscription = forward(operation).subscribe(observer);
        },
        error: (error) => observer.error(error),
      });

      return () => {
        tokenSubscription.unsubscribe();
        retrySubscription?.unsubscribe();
      };
    });
  });

  return {
    link: from([authLink, tokenErrorLink, httpLink.create({ uri })]),
    cache: new InMemoryCache({
      addTypename: false
    }),
    defaultOptions
  };
}

@NgModule({
  providers: [
    Apollo,
    {
      provide: APOLLO_OPTIONS,
      useFactory: createApollo,
      deps: [HttpLink, AuthService],
    },
  ],
})
export class GraphQLModule {}
